import { format } from "date-fns";

import { formatPhoneNumber } from "utils/common";

import { lastTenDigits, webrtcDeviceUserId } from "./phone-match.util";

// Shared display helpers for DialStack calls (practice /calls page + patient Calls tab).

export const STATUS_LABELS: Record<string, string> = {
  completed: "Completed",
  no_answer: "No answer",
  busy: "Busy",
  failed: "Failed",
  voicemail: "Voicemail"
};

export const STATUS_COLORS: Record<
  string,
  "success" | "warning" | "error" | "default"
> = {
  completed: "success",
  no_answer: "warning",
  busy: "warning",
  failed: "error",
  voicemail: "default"
};

export const DIRECTION_LABELS: Record<string, string> = {
  inbound: "Inbound",
  outbound: "Outbound"
};

// Call numbers arrive as E.164 (+1...); formatPhoneNumber only handles bare
// 10-digit values, so format the last ten and fall back to the raw string.
// A value with letters in it (a web softphone's "user_…-wrtc" identity) is
// not a phone number and is left alone rather than squeezed into one.
export function displayNumber(value: string) {
  const lastTen = lastTenDigits(value);
  return formatPhoneNumber(lastTen) || value;
}

// The number line under a live call's header. A web softphone's device id is
// nothing anyone can read or dial back, and the colleague card under the
// header already names who it is (ENG-2061), so it gives an empty line.
export function displayCallerNumber(value: string) {
  return webrtcDeviceUserId(value) ? "" : displayNumber(value);
}

export function formatDuration(seconds: number | null) {
  if (seconds === null || seconds === undefined) return null;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

// One line summarizing a call for a detail header, e.g.
// "Inbound · completed · 0:22 · Aug 14, 2026 5:06 PM".
export function callSubtitle(call: {
  direction: "inbound" | "outbound";
  status: string | null;
  duration_seconds: number | null;
  started_at: string | null;
  created_at: string;
}) {
  const direction = call.direction === "inbound" ? "Inbound" : "Outbound";
  const status =
    call.status && STATUS_LABELS[call.status]
      ? STATUS_LABELS[call.status].toLowerCase()
      : null;
  const when = format(
    new Date(call.started_at ?? call.created_at),
    "MMM d, yyyy h:mm a"
  );
  return [direction, status, formatDuration(call.duration_seconds), when]
    .filter(Boolean)
    .join(" · ");
}

// List rows carry the staff user as a bare id; only the detail nests names.
export function staffUserName(
  user: { first_name: string; last_name: string } | number | null | undefined
) {
  if (!user || typeof user !== "object") return null;
  return `${user.first_name} ${user.last_name}`.trim() || null;
}
